import { inNativeApp, requestNativePushToken } from '@/lib/native-app';
import { registerPushDevice, removePushDevice } from '@/lib/push-device-api';

/**
 * Push notifications in the phone app (T-45): once someone is signed in inside the app, this phone gets
 * their notifications; signing out stops them. Outside the app both do nothing.
 *
 * The server is what guarantees the stop: a phone is registered under the sign-in session it sent the
 * PUT with, and only phones whose session is still live get pushes (worker push_device.auth_session_id),
 * so signing out ends them even when the DELETE below never arrives. The DELETE only tidies up early.
 *
 * Sign-out can come while a registration is still on its way (waiting for the person to allow
 * notifications, or for the PUT). Each sign-out starts a new generation: a registration from an older
 * one stops before its PUT, or removes the token straight after it, so a signed-out phone never stays
 * registered.
 */

/** The token this page registered, so sign-out can remove it. */
let registeredToken: string | null = null;
/** The token a registration is sending right now (its PUT hasn't answered). */
let sendingToken: string | null = null;
let registering: Promise<void> | null = null;
let generation = 0;

async function removeQuietly(token: string): Promise<void> {
  try {
    await removePushDevice(token, AbortSignal.timeout(5_000));
  } catch {
    // Signing out goes on; the token moves to whoever signs in next, or Expo drops it.
  }
}

export function registerThisPhone(): Promise<void> {
  if (!inNativeApp()) return Promise.resolve();
  registering ??= (async () => {
    const mine = generation;
    try {
      const answer = await requestNativePushToken();
      if (!answer || mine !== generation) return;
      sendingToken = answer.token;
      const res = await registerPushDevice(answer.token, answer.platform);
      sendingToken = null;
      if (res.error) return;
      // Signed out while the PUT was out: take it back.
      if (mine !== generation) await removeQuietly(answer.token);
      else registeredToken = answer.token;
    } finally {
      sendingToken = null;
      registering = null;
    }
  })();
  return registering;
}

/** Signing out: this phone stops getting the person's notifications. Bounded, never throws. */
export async function forgetThisPhone(): Promise<void> {
  generation += 1;
  // A PUT still out finishes against the old generation and removes its own token (above); remove it
  // now too, in case it already landed and its answer is what's slow.
  const tokens = [registeredToken, sendingToken].filter((t): t is string => !!t);
  registeredToken = null;
  await Promise.all([...new Set(tokens)].map(removeQuietly));
}
